import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import { AdminQueueState } from './ui/AdminQueueState';
import apiService from '../services/ApiService';
import type {
  LimitedDesignationDefinition,
  LimitedDesignationIssuance,
  LimitedDesignationSupply,
  LimitedDesignationVehicleSearchResult,
} from '../services/api/limitedDesignations';
import { resolvePublicFileUrl } from '../utils/imageUrls';
import {
  formatLimitedDesignationOrdinal,
  formatLimitedDesignationTitle,
} from '../utils/limitedDesignationLabels';

export const AdminLimitedDesignations: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<LimitedDesignationDefinition[]>([]);
  const [issuances, setIssuances] = useState<LimitedDesignationIssuance[]>([]);
  const [supply, setSupply] = useState<LimitedDesignationSupply | null>(null);
  const [foundingDef, setFoundingDef] = useState<LimitedDesignationDefinition | null>(null);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<LimitedDesignationVehicleSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<LimitedDesignationVehicleSearchResult | null>(null);
  const [eligibility, setEligibility] = useState<{
    eligible: boolean;
    reason?: string | null;
    next_ordinal?: number | null;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [publicReason, setPublicReason] = useState('');
  const [confirmIssue, setConfirmIssue] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const [revokeId, setRevokeId] = useState<number | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeConfirm, setRevokeConfirm] = useState('');

  const [correctId, setCorrectId] = useState<number | null>(null);
  const [correctVehicleId, setCorrectVehicleId] = useState('');
  const [correctReason, setCorrectReason] = useState('');
  const [correctConfirm, setCorrectConfirm] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const overview = await apiService.adminLimitedDesignationOverview();
      setDefinitions(overview.definitions || []);
      const founding =
        overview.definitions.find((d) => d.key === 'founding_vehicle') || overview.definitions[0] || null;
      setFoundingDef(founding);
      if (founding?.id) {
        const issued = await apiService.adminLimitedDesignationIssuances(founding.id);
        setIssuances(issued.issuances || []);
        setSupply(issued.supply);
        if (issued.definition) setFoundingDef(issued.definition as LimitedDesignationDefinition);
      } else {
        setIssuances([]);
        setSupply(null);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load limited designations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selected || !foundingDef?.id) {
      setEligibility(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const el = await apiService.adminLimitedDesignationEligibility(foundingDef.id, selected.id);
        if (!cancelled) setEligibility(el);
      } catch {
        if (!cancelled) setEligibility({ eligible: false, reason: 'Could not check eligibility' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, foundingDef?.id]);

  const runSearch = async () => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const found = await apiService.adminLimitedDesignationSearchVehicles(search.trim());
      setResults(found);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const issue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!foundingDef?.id || !selected) {
      setFormError('Select a designation and vehicle.');
      return;
    }
    if (confirmIssue !== 'ISSUE') {
      setFormError('Type ISSUE to confirm.');
      return;
    }
    setSaving(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      const data = await apiService.adminLimitedDesignationIssue({
        definition_id: foundingDef.id,
        vehicle_id: selected.id,
        reason: reason.trim() || undefined,
        public_reason: publicReason.trim() || undefined,
      });
      setFormSuccess(
        `Issued ${formatLimitedDesignationTitle(data.name, data.ordinal, data.edition_size)}`
      );
      setSelected(null);
      setReason('');
      setPublicReason('');
      setConfirmIssue('');
      setResults([]);
      setSearch('');
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Issuance failed');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!revokeId) return;
    if (revokeConfirm !== 'REVOKE') {
      setFormError('Type REVOKE to confirm.');
      return;
    }
    if (!revokeReason.trim()) {
      setFormError('Revocation reason required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await apiService.adminLimitedDesignationRevoke(revokeId, revokeReason.trim());
      setFormSuccess('Designation revoked. Ordinal is not reused.');
      setRevokeId(null);
      setRevokeReason('');
      setRevokeConfirm('');
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Revoke failed');
    } finally {
      setSaving(false);
    }
  };

  const correct = async () => {
    if (!correctId) return;
    if (correctConfirm !== 'CORRECT') {
      setFormError('Type CORRECT to confirm.');
      return;
    }
    const vid = parseInt(correctVehicleId, 10);
    if (!vid || !correctReason.trim()) {
      setFormError('New vehicle id and reason required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await apiService.adminLimitedDesignationCorrect({
        issuance_id: correctId,
        vehicle_id: vid,
        reason: correctReason.trim(),
      });
      setFormSuccess('Correction applied. Same ordinal, new vehicle.');
      setCorrectId(null);
      setCorrectVehicleId('');
      setCorrectReason('');
      setCorrectConfirm('');
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Correction failed');
    } finally {
      setSaving(false);
    }
  };

  const foundingSupply = useMemo(() => {
    return supply || foundingDef?.supply || null;
  }, [supply, foundingDef]);

  if (loading) {
    return (
      <div className="p-6">
        <PageHeader
          eyebrow="Admin"
          title="Limited designations"
          description="Finite official recognition"
        />
        <p className="text-sm text-gray-500 mt-4">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <PageHeader
        eyebrow="Admin"
        title="Limited designations"
        description="Admin-issued finite editions. Founding Vehicle is 50 numbered builds."
      />

      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load limited designations"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}
      {formError ? <p className="text-sm text-red-400">{formError}</p> : null}
      {formSuccess ? <p className="text-sm text-emerald-400">{formSuccess}</p> : null}

      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-300">
          Feature status
        </h2>
        <p className="text-sm text-gray-400">
          Limited designations and Founding Vehicle are shipped final features (always on). Emergency
          off: set <code className="text-gray-300">LD_FLAG_*</code> to 0 on the server.
        </p>
        {(
          [
            ['limited_designations_enabled', 'Limited designations'],
            ['founding_vehicle_designation_enabled', 'Founding Vehicle'],
          ] as const
        ).map(([key, label]) => (
          <div key={key} className="flex items-center justify-between gap-3 text-sm text-gray-200">
            <span>
              {label}{' '}
              <span className="font-mono text-xs text-gray-500">({key})</span>
            </span>
            <span className="text-emerald-400">On · shipped</span>
          </div>
        ))}
      </section>

      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-300">
          {foundingDef?.name || 'Founding Vehicle'} supply
        </h2>
        {foundingSupply ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Issued</p>
              <p className="text-xl text-white font-semibold">{foundingSupply.issued}</p>
            </div>
            <div>
              <p className="text-gray-500">Remaining</p>
              <p className="text-xl text-white font-semibold">{foundingSupply.remaining}</p>
            </div>
            <div>
              <p className="text-gray-500">Edition</p>
              <p className="text-xl text-white font-semibold">{foundingSupply.edition_size}</p>
            </div>
            <div>
              <p className="text-gray-500">Next</p>
              <p className="text-xl text-white font-semibold">
                {foundingSupply.edition_complete
                  ? 'Edition complete'
                  : foundingSupply.next_ordinal ?? '—'}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Founding Vehicle definition not found. Apply migration.</p>
        )}
        <ul className="text-xs text-gray-500 list-disc pl-4">
          {definitions.map((d) => (
            <li key={d.id}>
              {d.key}: {d.status}, edition {d.edition_size}
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-orange-500/20 bg-black/40 p-5 space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-orange-200/90">
          Issue next Founding Vehicle
        </h2>
        <p className="text-sm text-gray-400">
          Server assigns the next ordinal. This cannot be undone without revoke or correction. Type ISSUE to
          confirm.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            className="flex-1 min-w-[200px] rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            placeholder="Search build name, owner, username, make, URL…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void runSearch();
              }
            }}
          />
          <Button type="button" onClick={() => void runSearch()} disabled={searching}>
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </div>
        {results.length > 0 && (
          <ul className="space-y-2 max-h-64 overflow-auto">
            {results.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => setSelected(v)}
                  className={`w-full text-left rounded-lg border px-3 py-2 flex gap-3 items-center ${
                    selected?.id === v.id
                      ? 'border-orange-500/50 bg-orange-500/10'
                      : 'border-white/10 bg-white/[0.02]'
                  }`}
                >
                  {v.hero_image_url ? (
                    <img
                      src={resolvePublicFileUrl(v.hero_image_url) || v.hero_image_url}
                      alt=""
                      className="h-12 w-16 object-cover rounded"
                    />
                  ) : (
                    <div className="h-12 w-16 rounded bg-white/5" />
                  )}
                  <div className="min-w-0 text-sm">
                    <p className="text-white font-medium truncate">
                      {v.nickname || `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim()}
                    </p>
                    <p className="text-gray-400 truncate">
                      {v.year} {v.make} {v.model} · @{v.username || 'unknown'}
                      {v.public_url ? ` · ${v.public_url}` : ''}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {selected && (
          <form onSubmit={issue} className="space-y-3 border-t border-white/10 pt-4">
            <p className="text-sm text-gray-300">
              Selected vehicle #{selected.id}
              {eligibility
                ? eligibility.eligible
                  ? ` · eligible for ${formatLimitedDesignationOrdinal(
                      eligibility.next_ordinal || 0,
                      foundingSupply?.edition_size || 50
                    )}`
                  : ` · not eligible: ${eligibility.reason || 'unknown'}`
                : ''}
            </p>
            <textarea
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              placeholder="Internal reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
            <textarea
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              placeholder="Public reason (optional)"
              value={publicReason}
              onChange={(e) => setPublicReason(e.target.value)}
              rows={2}
            />
            <input
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white font-mono"
              placeholder='Type ISSUE to confirm'
              value={confirmIssue}
              onChange={(e) => setConfirmIssue(e.target.value)}
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={
                saving ||
                !eligibility?.eligible ||
                Boolean(foundingSupply?.edition_complete) ||
                confirmIssue !== 'ISSUE'
              }
            >
              {saving ? 'Issuing…' : 'Issue next ordinal'}
            </Button>
          </form>
        )}
      </section>

      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-300">Issued vehicles</h2>
        {issuances.length === 0 ? (
          <p className="text-sm text-gray-500">No issuances yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="text-gray-500">
                <tr>
                  <th className="py-2 pr-3">Ordinal</th>
                  <th className="py-2 pr-3">Vehicle</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Issued</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {issuances.map((row) => (
                  <tr key={row.public_id} className="border-t border-white/5 text-gray-200">
                    <td className="py-2 pr-3 font-mono">
                      {formatLimitedDesignationOrdinal(row.ordinal, row.edition_size)}
                    </td>
                    <td className="py-2 pr-3">
                      {row.vehicle?.nickname ||
                        `${row.vehicle?.year || ''} ${row.vehicle?.make || ''} ${row.vehicle?.model || ''}`.trim() ||
                        `#${row.subject_id}`}
                      {row.vehicle?.username ? (
                        <span className="text-gray-500"> · @{row.vehicle.username}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3">{row.status}</td>
                    <td className="py-2 pr-3">{row.issued_at}</td>
                    <td className="py-2 space-x-2">
                      {row.status === 'active' && row.id ? (
                        <button
                          type="button"
                          className="text-red-300 hover:underline"
                          onClick={() => {
                            setRevokeId(row.id!);
                            setRevokeReason('');
                            setRevokeConfirm('');
                          }}
                        >
                          Revoke
                        </button>
                      ) : null}
                      {row.id ? (
                        <button
                          type="button"
                          className="text-amber-200/80 hover:underline"
                          onClick={() => {
                            setCorrectId(row.id!);
                            setCorrectVehicleId('');
                            setCorrectReason('');
                            setCorrectConfirm('');
                          }}
                        >
                          Correct
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {revokeId !== null && (
          <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-4 space-y-2">
            <p className="text-sm text-red-200">
              Revoke issuance #{revokeId}. Public display stops. Ordinal is not released.
            </p>
            <textarea
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              placeholder="Revocation reason (required)"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
            <input
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm font-mono text-white"
              placeholder="Type REVOKE"
              value={revokeConfirm}
              onChange={(e) => setRevokeConfirm(e.target.value)}
            />
            <div className="flex gap-2">
              <Button type="button" onClick={() => void revoke()} disabled={saving}>
                Confirm revoke
              </Button>
              <Button type="button" variant="secondary" onClick={() => setRevokeId(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {correctId !== null && (
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-2">
            <p className="text-sm text-amber-100">
              Administrative correction for issuance #{correctId}. Reassigns the vehicle while keeping the same
              ordinal. Use only for operational mistakes.
            </p>
            <input
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              placeholder="New vehicle id"
              value={correctVehicleId}
              onChange={(e) => setCorrectVehicleId(e.target.value)}
            />
            <textarea
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              placeholder="Correction reason (required)"
              value={correctReason}
              onChange={(e) => setCorrectReason(e.target.value)}
            />
            <input
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm font-mono text-white"
              placeholder="Type CORRECT"
              value={correctConfirm}
              onChange={(e) => setCorrectConfirm(e.target.value)}
            />
            <div className="flex gap-2">
              <Button type="button" onClick={() => void correct()} disabled={saving}>
                Confirm correction
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCorrectId(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminLimitedDesignations;
